// A marriage offered by an ally: the two faces, and the two answers.
//
// Like the merchant offer, it lives in the snapshot (`globals.dynasty`), so an
// answered, expired or voided offer leaves the screen with no event to miss.
import { Button } from '@aod/ui/components/ui/button';
import { type Dynasty, proposalTo, royalById } from '../dynasty/dynasty.js';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { RoyalPortrait } from './RoyalPortrait.js';

export function MarriageProposalToast({
  dynasty,
  from,
  localSeat,
  fromName,
  onAccept,
  onDecline,
}: {
  readonly dynasty: Dynasty | null;
  /** The ally who made the offer; null when none is on the table. */
  readonly from: number | null;
  readonly localSeat: number;
  readonly fromName: string;
  readonly onAccept: (from: number) => void;
  readonly onDecline: (from: number) => void;
}) {
  const t = useT();
  if (!dynasty || from === null) return null;
  const offer = proposalTo(dynasty, localSeat);
  const theirs = royalById(dynasty, offer?.royalFrom ?? null);
  const yours = royalById(dynasty, offer?.royalTo ?? null);
  const rank = (female: boolean | undefined) =>
    t(female ? 'dynasty.princess' : 'dynasty.prince') as string;
  const label = (r: typeof theirs, house: string) =>
    t('dynasty.ofHouse', { rank: rank(r?.female), house });
  return (
    <div
      data-testid="marriage-proposal-toast"
      {...appDom({ id: 'marriage-proposal-toast', type: 'Toast', scope: 'toast' })}
      className="pointer-events-auto flex w-full max-w-[min(92vw,460px)] flex-wrap items-center gap-3 rounded-xl border border-amber-500/50 bg-panel-scrim px-4 py-2.5 text-left text-foreground shadow-[0_0_24px_rgba(240,200,120,0.16)] backdrop-blur-sm dark:shadow-[0_0_30px_rgba(240,200,120,0.22)]"
    >
      <span className="flex shrink-0 gap-1.5">
        <RoyalPortrait
          portrait={theirs?.portrait ?? null}
          label={label(theirs, fromName)}
          stage={theirs?.stage}
        />
        <RoyalPortrait
          portrait={yours?.portrait ?? null}
          label={label(yours, t('dynasty.court') as string)}
          stage={yours?.stage}
        />
      </span>
      <span className="flex min-w-[10rem] flex-1 flex-col">
        <span className="font-serif leading-tight text-sm text-amber-700 dark:text-amber-300">
          {t('dynasty.offerTitle', { house: fromName })}
        </span>
        <span className="text-xs leading-snug text-muted-foreground">
          {t('dynasty.offerBody', { theirs: rank(theirs?.female), yours: rank(yours?.female) })}
        </span>
      </span>
      <span className="flex shrink-0 gap-2">
        <Button type="button" size="sm" onClick={() => onAccept(from)}>
          {t('dynasty.accept')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => onDecline(from)}>
          {t('dynasty.decline')}
        </Button>
      </span>
    </div>
  );
}
